import { useEffect, useRef, useState } from 'react';
import { Plus, Settings as SettingsIcon } from 'lucide-react';
import { apiDelete, apiGet, apiPost, apiPut, axiosData } from '../lib/api-client';
import { useDirty } from '../lib/dirty';
import { applyTableQuery, columnParam, nextSort, pageCount, pageSlice, readPageSize, writePageSize, type ColumnSort } from '../lib/list-query';
import { apiError } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Alert, Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ExcelButton, PageHead, PageSizeSelect, SortHeader } from '../components/ui/chrome';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../components/ui/ColumnSearch';
import { Dialog } from '../components/ui/Dialog';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Field, Input, Select, TextArea } from '../components/ui/Input';
import { useNotice } from '../components/ui/Notice';

interface ModelParamValue { value: string; displayName?: string }
interface ModelParameter { id: string; displayName?: string; values: ModelParamValue[] }
interface ModelVariant { displayName: string; description?: string; isDefault?: boolean; params: { id: string; value: string }[] }
interface CursorModel { id: string; displayName: string; description?: string; aliases: string[]; parameters: ModelParameter[]; variants: ModelVariant[] }
interface ToolDef { name: string; group: string; title: string; description: string }
interface AgentForm { code: string; title: string; model: string; tools: string[]; training: string[]; skills: { id: string; title: string; body: string }[]; modelParams: { id: string; value: string }[] }
type Role = AgentForm

const GROUP_TITLES: Record<string, string> = { overview: 'Overview', ar: 'AR', quotes: 'Quotations', ap: 'AP', ops: 'Inbox / ops', reports: 'Reports', integrations: 'Integrations', ui: 'UI' };

function findModel(models: CursorModel[], id: string) {
  const needle = String(id || '').trim().replace(/^cursor:/i, '');
  return models.find((model) => model.id === needle) || models.find((model) => (model.aliases || []).includes(needle));
}

function paramKey(params: { id: string; value: string }[]) {
  return [...params].filter((item) => item.id).sort((a, b) => a.id.localeCompare(b.id)).map((item) => `${item.id}=${item.value}`).join('|');
}

function paramBits(model: CursorModel | undefined, params: { id: string; value: string }[]) {
  if (!model) return [];
  const bits: string[] = [];
  for (const param of params) {
    const def = (model.parameters || []).find((item) => item.id === param.id);
    const val = def?.values?.find((item) => item.value === param.value);
    const raw = (val?.displayName || (param.value === 'true' ? def?.displayName || param.id : param.value === 'false' || param.value === '' ? '' : param.value) || '').replace(/[\u200b\u200c\u200d\ufeff]/g, '').trim();
    if (raw && !bits.includes(raw)) bits.push(raw);
  }
  return bits;
}

function version(id: string) {
  const match = /^grok-(\d+(?:\.\d+)*)$/.exec(id);
  if (!match) return 0;
  return match[1].split('.').reduce((acc, part) => acc * 1000 + Number(part), 0);
}

function defaultModel(models: CursorModel[]) {
  const groks = models.filter((model) => /^grok-\d/.test(model.id)).sort((a, b) => version(b.id) - version(a.id));
  return groks[0] || models[0];
}

function defaultParams(model?: CursorModel) {
  if (!model) return [];
  const high = model.variants.find((variant) => {
    const effort = variant.params.find((param) => param.id === 'reasoning_effort' || param.id === 'effort');
    const fast = variant.params.find((param) => param.id === 'fast');
    const context = variant.params.find((param) => param.id === 'context');
    return effort?.value === 'high' && fast?.value !== 'true' && (!context || context.value === '256k');
  });
  const variant = high || model.variants.find((item) => item.isDefault) || model.variants[0];
  return variant ? variant.params.map((param) => ({ ...param })) : [];
}

function copyRole(role: Role, models: CursorModel[]): AgentForm {
  const model = findModel(models, role.model);
  return {
    code: role.code,
    title: role.title,
    model: model?.id || role.model,
    tools: [...(role.tools || [])],
    training: [...(role.training || [])],
    skills: (role.skills || []).map((skill) => ({ ...skill })),
    modelParams: [...(role.modelParams || [])],
  };
}

function modelLabel(models: CursorModel[], role: { model: string; modelParams?: { id: string; value: string }[] }) {
  const model = findModel(models, role.model);
  const bits = paramBits(model, role.modelParams || []);
  const name = model ? (model.displayName === model.id ? model.id : `${model.displayName} (${model.id})`) : role.model || 'unset';
  return bits.length ? `${name} · ${bits.join(' · ')}` : name;
}

export function AgentsPage() {
  const { can } = useAuth();
  const feedback = useNotice();
  const dirty = useDirty();
  const baseline = useRef('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [syncState, setSyncState] = useState<{ roleCode: string; state: string; dirtyReason?: string }[]>([]);
  const [files, setFiles] = useState<{ path: string }[]>([]);
  const [form, setForm] = useState<AgentForm | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ColumnSort | null>(null);
  const columnSearch = useColumnSearch();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => readPageSize('agents'));
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [models, setModels] = useState<CursorModel[]>([]);
  const [modelsConfigured, setModelsConfigured] = useState(false);
  const [modelsError, setModelsError] = useState('');
  const [catalog, setCatalog] = useState<ToolDef[]>([]);
  const [ruleDraft, setRuleDraft] = useState('');
  const [skillTitle, setSkillTitle] = useState('');
  const [skillBody, setSkillBody] = useState('');

  function reload() {
    void apiGet<{ roles: Role[]; sync: { roleCode: string; state: string; dirtyReason?: string }[] }>('/api/admin/roles').then((data) => {
      setRoles(data.roles || []);
      setSyncState(data.sync || []);
      setError('');
    }).catch((err) => setError(apiError(err, 'Could not load agents'))).finally(() => setReady(true));
  }

  useEffect(() => {
    reload();
    void apiGet<{ tools: ToolDef[] }>('/api/admin/tool-catalog').then((data) => setCatalog(data.tools || [])).catch(() => setCatalog([]));
    void apiGet<{ models: CursorModel[]; configured?: boolean }>('/api/admin/cursor/models').then((data) => {
      setModels(data.models || []);
      setModelsConfigured(Boolean(data.configured));
      setModelsError('');
    }).catch((err) => {
      const data = axiosData<{ models?: CursorModel[]; configured?: boolean; error?: string }>(err);
      setModels(data?.models || []);
      setModelsConfigured(Boolean(data?.configured));
      setModelsError(data?.error || 'Could not load Cursor models');
    });
  }, []);

  const writable = can('ai_config', 'manage');
  const groups = [...new Set(catalog.map((tool) => tool.group))];
  const agentColumns = [
    { key: 'title', label: 'Title', text: (role: Role) => role.title },
    { key: 'code', label: 'Code', text: (role: Role) => role.code },
    { key: 'model', label: 'Model', text: (role: Role) => modelLabel(models, role) },
    { key: 'sync', label: 'Sync', text: (role: Role) => { const sync = syncState.find((item) => item.roleCode === role.code); return `${sync?.state || ''} ${sync?.dirtyReason || ''}`; } },
  ];
  const filtered = applyTableQuery(roles, search, agentColumns, sort).filter((role) => columnSearch.matches(Object.fromEntries(agentColumns.map((column) => [column.key, String(column.text(role) ?? '')]))));
  const agentKey = columnParam(columnSearch.applied);
  useEffect(() => { setPage(1); }, [agentKey]);
  const pages = pageCount(filtered.length, pageSize);
  const currentPage = Math.min(page, pages);
  const paged = pageSlice(filtered, currentPage, pageSize);
  const formKey = form ? JSON.stringify(form) : '';

  function patch(next: Partial<AgentForm> | ((current: AgentForm) => AgentForm)) {
    setForm((current) => {
      if (!current) return current;
      return typeof next === 'function' ? next(current) : { ...current, ...next };
    });
  }

  function selected(current: AgentForm) {
    return findModel(models, current.model);
  }

  function adopt(next: AgentForm, nextNew: boolean) {
    baseline.current = JSON.stringify(next);
    setIsNew(nextNew);
    setForm(next);
    setRuleDraft('');
    setSkillTitle('');
    setSkillBody('');
    dirty.setDirty(false);
  }

  function openRole(role: Role) {
    dirty.requestLeave(() => adopt(copyRole(role, models), false));
  }

  function showList() {
    dirty.requestLeave(() => {
      setForm(null);
      baseline.current = '';
      dirty.setDirty(false);
    });
  }

  function startNew() {
    const model = defaultModel(models);
    dirty.requestLeave(() => adopt({ code: '', title: '', model: model?.id || '', tools: ['report.preview'], training: [], skills: [], modelParams: defaultParams(model) }, true));
  }

  async function save() {
    if (!form) return;
    if (!form.model) {
      feedback.failure('Pick a model from the live Cursor catalog.');
      return;
    }
    setError('');
    try {
      const saved = isNew ? await apiPost<Role>('/api/admin/roles', form) : await apiPut<Role>(`/api/admin/roles/${form.code}`, form);
      adopt(copyRole(saved, models), false);
      reload();
      feedback.success('Saved');
    } catch (err) {
      feedback.failure(apiError(err, 'Save failed'));
    }
  }

  async function remove(role: Role) {
    setError('');
    try {
      await apiDelete(`/api/admin/roles/${role.code}`);
      setPendingDelete(null);
      if (form?.code === role.code) {
        setForm(null);
        baseline.current = '';
        dirty.setDirty(false);
      }
      reload();
      feedback.success('Deleted');
    } catch (err) {
      feedback.failure(apiError(err, 'Delete failed'));
    }
  }

  useEffect(() => {
    if (!form) {
      dirty.setDirty(false);
      dirty.setSaver(null);
      return;
    }
    const changed = formKey !== baseline.current;
    dirty.setDirty(changed);
    dirty.setSaver(changed ? save : null);
    return () => {
      dirty.setDirty(false);
      dirty.setSaver(null);
    };
  }, [formKey]);

  useEffect(() => {
    const onLeave = (event: BeforeUnloadEvent) => {
      if (!dirty.dirty) return;
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [dirty.dirty]);

  const actionItems = [
    ...(writable ? [{ label: 'New', icon: <Plus className="h-4 w-4" />, onClick: startNew }] : []),
    { label: 'Sync preview', onClick: () => void feedback.run('Previewing the Cursor sync', async () => { setFiles((await apiGet<{ files: { path: string }[] }>('/api/admin/roles/sync/preview')).files); }, 'Sync preview ready') },
    { label: 'Sync to Cursor', onClick: () => void feedback.run('Syncing agents to Cursor', async () => {
      const data = await apiPost<{ files: { path: string }[]; main?: { url?: string } }>('/api/admin/roles/sync', {});
      setFiles(data.files);
      reload();
      return data;
    }, (data) => data.main?.url ? `Synced to main: ${data.main.url}` : 'Wrote native Cursor files in the API repo. GitHub did not update main.') },
  ];

  return (
    <div className="space-y-4">
      <PageHead
        crumbs={[{ label: 'Agents' }]}
        title="Agents"
        subtitle={`${filtered.length} ${filtered.length === 1 ? 'record' : 'records'}`}
        actionsInline
        actions={(
          <DropdownMenu
            className="h-full"
            trigger={<Button variant="outline" className="h-full gap-2"><SettingsIcon className="h-4 w-4" />Actions</Button>}
            items={actionItems}
          />
        )}
      />
      <div className={form ? 'max-md:hidden' : ''}>
      <div className="flex flex-wrap items-center gap-3">
        <Input className="h-8 max-w-xs" placeholder="Search" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
      </div>
      {error ? <Alert error>{error}</Alert> : null}
      <div className="mb-3 mt-3 flex items-center justify-between gap-3 text-sm">
        <span className="flex items-center gap-3"><PageSizeSelect value={pageSize} onChange={(size) => { setPageSize(size); writePageSize('agents', size); setPage(1); }} /><ExcelButton filename="Agents" headers={['Title', 'Code', 'Model', 'Sync']} rows={filtered.map((role) => { const sync = syncState.find((item) => item.roleCode === role.code); return [role.title, role.code, modelLabel(models, role), `${sync?.state || ''}${sync?.dirtyReason ? ` · ${sync.dirtyReason}` : ''}`]; })} /></span>
        <span className="text-[var(--bo-muted)]">Page {currentPage} of {pages}</span>
        <span className="flex gap-2">
          <Button size="xs" variant="outline" disabled={currentPage <= 1} onClick={() => setPage((n) => Math.min(n, pages) - 1)}>Prev</Button>
          <Button size="xs" variant="outline" disabled={currentPage >= pages} onClick={() => setPage(currentPage + 1)}>Next</Button>
        </span>
      </div>
      </div>
      <div className={form ? 'grid items-stretch gap-4 max-md:block md:h-[calc(100vh-16rem)] xl:grid-cols-[3fr_2fr]' : ''}>
        <div className={`min-h-0 min-w-0 flex-1 overflow-auto rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] ${form ? 'max-md:hidden' : ''}`}>
          <table className="bo-cards min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800/80 dark:text-gray-400">
              <tr>
                <SortHeader label="Title" active={sort?.key === 'title'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'title')); setPage(1); }} />
                <SortHeader label="Code" active={sort?.key === 'code'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'code')); setPage(1); }} />
                <SortHeader label="Model" active={sort?.key === 'model'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'model')); setPage(1); }} />
                <SortHeader label="Sync" active={sort?.key === 'sync'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'sync')); setPage(1); }} />
                <th className="px-4 py-3 text-right font-medium"><span className="mr-2">Actions</span><ColumnFilterToggle open={columnSearch.open} onClick={columnSearch.toggle} /></th>
              </tr>
              {columnSearch.open ? <ColumnFilterInputs columns={agentColumns.map((column) => ({ key: column.key, label: column.label }))} draft={columnSearch.draft} onChange={columnSearch.set} trailing /> : null}
            </thead>
            <tbody>
              {paged.map((role) => {
                const sync = syncState.find((item) => item.roleCode === role.code);
                return (
                  <tr key={role.code} className="cursor-pointer border-t border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/60" onClick={() => openRole(role)}>
                    <td className="px-3 py-2" data-label="Title">{role.title}</td>
                    <td className="px-3 py-2" data-label="Code">{role.code}</td>
                    <td className="px-3 py-2" data-label="Model">{modelLabel(models, role)}</td>
                    <td className="px-3 py-2" data-label="Sync">{sync?.state || ''}{sync?.dirtyReason ? ` · ${sync.dirtyReason}` : ''}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right" data-label="">
                      <span className="inline-flex h-7 items-center gap-2">
                        <Button onClick={(event) => { event.stopPropagation(); openRole(role); }}>Edit</Button>
                        {writable ? <Button variant="destructive" onClick={(event) => { event.stopPropagation(); setPendingDelete(role); }}>Delete</Button> : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filtered.length ? <p className="px-4 py-8 text-sm text-gray-500">{ready ? 'No records yet.' : 'Loading'}</p> : null}
        </div>
        {form ? (
          <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] max-md:h-auto max-md:overflow-visible">
            <div className="flex items-center justify-between border-b border-[var(--bo-border)] px-4 py-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--bo-faint)]">Agent</div>
                <div className="font-semibold">{form.title || 'New agent'}</div>
              </div>
              <Button variant="outline" onClick={showList}><span className="md:hidden">Return to list</span><span className="hidden md:inline">Close</span></Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4 max-md:overflow-visible">{editor()}</div>
          </div>
        ) : null}
      </div>
      {files.length ? <ul className="text-xs text-gray-500">{files.map((file) => <li key={file.path}>{file.path}</li>)}</ul> : null}
      {pendingDelete ? (
        <Dialog
          title="Delete record"
          onClose={() => setPendingDelete(null)}
          footer={<><Button variant="outline" onClick={() => setPendingDelete(null)}>Cancel</Button><Button variant="destructive" onClick={() => void remove(pendingDelete)}>Delete</Button></>}
        >
          <p className="text-sm text-gray-600 dark:text-gray-300">Delete {pendingDelete.title || pendingDelete.code}?</p>
        </Dialog>
      ) : null}
    </div>
  );

  function editor() {
    if (!form) return null;
    return (
      <div className="space-y-3">
        <div className="flex h-7 items-center justify-end gap-2">
          {writable ? <Button onClick={() => void save()}>Save</Button> : null}
          {writable && !isNew ? <Button variant="destructive" onClick={() => { const role = roles.find((item) => item.code === form.code); if (role) setPendingDelete(role); }}>Delete</Button> : null}
        </div>
        <Field label="Code"><Input value={form.code} readOnly={!isNew} onChange={(event) => patch({ code: event.target.value })} /></Field>
        <Field label="Title"><Input value={form.title} onChange={(event) => patch({ title: event.target.value })} /></Field>
        <Field label="Model">
          {models.length ? (
            <Select searchable value={form.model} onChange={(event) => { const model = findModel(models, event.target.value); patch({ model: event.target.value, modelParams: defaultParams(model) }); }}>
              {form.model && !findModel(models, form.model) ? <option value={form.model}>{form.model} (not in live catalog)</option> : null}
              {models.map((model) => <option key={model.id} value={model.id}>{model.displayName}</option>)}
            </Select>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800">{modelsConfigured ? `Live Cursor catalog is empty. ${modelsError || 'Try again after checking the API key.'}` : 'Set CURSOR_API_KEY to load the live Cursor model list. Free-text model ids are not used.'}</div>
          )}
          {selected(form)?.description ? <span className="mt-1 block text-xs text-gray-500">{selected(form)?.description}</span> : null}
        </Field>
        {selected(form)?.variants?.length ? (
          <Field label="Level">
            <Select value={String(selected(form)!.variants.findIndex((variant) => paramKey(variant.params) === paramKey(form.modelParams)))} onChange={(event) => { const variant = selected(form)?.variants[Number(event.target.value)]; if (variant) patch({ modelParams: variant.params.map((param) => ({ ...param })) }); }}>
              {selected(form)!.variants.map((variant, index) => <option key={index} value={index}>{`${variant.displayName || selected(form)?.displayName || selected(form)?.id || ''}${paramBits(selected(form), variant.params).length ? ` · ${paramBits(selected(form), variant.params).join(' · ')}` : ''}${variant.isDefault ? ' (default)' : ''}`}</option>)}
              {selected(form)!.variants.findIndex((variant) => paramKey(variant.params) === paramKey(form.modelParams)) < 0 ? <option value="-1">Custom</option> : null}
            </Select>
          </Field>
        ) : null}
        {(selected(form)?.parameters || []).map((param) => (
          <Field key={param.id} label={param.displayName || param.id}>
            <Select value={form.modelParams.find((item) => item.id === param.id)?.value || ''} onChange={(event) => patch((current) => ({ ...current, modelParams: event.target.value === '' ? current.modelParams.filter((item) => item.id !== param.id) : [...current.modelParams.filter((item) => item.id !== param.id), { id: param.id, value: event.target.value }] }))}>
              {param.values.map((opt) => <option key={opt.value} value={opt.value}>{opt.displayName || opt.value}</option>)}
            </Select>
          </Field>
        ))}
        <div>
          <div className="mb-1 text-xs font-medium">Tools</div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.tools.includes('*')} onChange={(event) => patch({ tools: event.target.checked ? ['*'] : [] })} /> All tools</label>
          {!form.tools.includes('*') ? groups.map((group) => (
            <div key={group} className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{GROUP_TITLES[group] || group}</div>
              {catalog.filter((tool) => tool.group === group).map((tool) => (
                <label key={tool.name} className="mt-1 flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-0.5" checked={form.tools.includes(tool.name)} onChange={(event) => patch((current) => { const rest = current.tools.filter((name) => name !== '*' && name !== tool.name); return { ...current, tools: event.target.checked ? [...rest, tool.name] : rest }; })} />
                  <span><span className="font-medium">{tool.title}</span><span className="block text-xs text-gray-500">{tool.description}</span></span>
                </label>
              ))}
            </div>
          )) : null}
        </div>
        <div>
          <div className="mb-1 text-xs font-medium">Rules</div>
          <div className="flex gap-2"><Input className="h-8" value={ruleDraft} placeholder="Always quote Breedon POs" onChange={(event) => setRuleDraft(event.target.value)} /><Button size="sm" variant="outline" onClick={() => { const text = ruleDraft.trim(); if (!text) return; patch((current) => ({ ...current, training: [...current.training, text] })); setRuleDraft(''); }}>Add</Button></div>
          {form.training.map((rule) => <div key={rule} className="mt-2 flex justify-between rounded-lg bg-gray-100 px-3 py-2 text-sm dark:bg-gray-800"><span>{rule}</span><Button size="xs" variant="ghost" onClick={() => patch((current) => ({ ...current, training: current.training.filter((item) => item !== rule) }))}>Remove</Button></div>)}
        </div>
        <div>
          <div className="mb-1 text-xs font-medium">Skills</div>
          <Input className="mb-2 h-8" value={skillTitle} placeholder="Skill title" onChange={(event) => setSkillTitle(event.target.value)} />
          <TextArea value={skillBody} placeholder="What this role should do" onChange={(event) => setSkillBody(event.target.value)} />
          <Button size="sm" className="mt-2" variant="outline" onClick={() => { const title = skillTitle.trim(); const body = skillBody.trim(); if (!title || !body) return; const skillId = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `skill-${Date.now()}`; patch((current) => ({ ...current, skills: [...current.skills, { id: skillId, title, body }] })); setSkillTitle(''); setSkillBody(''); }}>Add skill</Button>
          {form.skills.map((skill) => (
            <div key={skill.id} className="mt-2 rounded-lg bg-gray-100 px-3 py-2 text-sm dark:bg-gray-800">
              <div className="flex gap-2"><Input className="h-7" value={skill.title} onChange={(event) => patch((current) => ({ ...current, skills: current.skills.map((item) => item.id === skill.id ? { ...item, title: event.target.value } : item) }))} /><Button size="xs" variant="ghost" onClick={() => patch((current) => ({ ...current, skills: current.skills.filter((item) => item.id !== skill.id) }))}>Remove</Button></div>
              <TextArea className="mt-2 text-xs" value={skill.body} onChange={(event) => patch((current) => ({ ...current, skills: current.skills.map((item) => item.id === skill.id ? { ...item, body: event.target.value } : item) }))} />
            </div>
          ))}
        </div>
        {(form.tools || []).length ? (
          <div className="text-xs">
            <div className="font-semibold text-gray-500">Tools</div>
            <div className="mt-1 flex flex-wrap gap-1">{form.tools.includes('*') ? <Badge>All tools</Badge> : form.tools.map((name) => <Badge key={name}>{catalog.find((tool) => tool.name === name)?.title || name}</Badge>)}</div>
          </div>
        ) : null}
      </div>
    );
  }
}
